import { BridgeKey } from '../interfaces/bridge-key';
import { BridgesIsland } from '../interfaces/bridges-island';
import {
  BridgesMap,
  createsCrossing,
  computeNeighborPairs,
  normalizeKey,
  raycast,
  serializeKey,
} from '../geometry/bridges-geometry';

export interface BridgesPoint {
  x: number;
  y: number;
}

export type BridgesPointerIntent =
  | { kind: 'none' }
  | { kind: 'preview'; key: BridgeKey }
  | { kind: 'commit'; key: BridgeKey }
  | { kind: 'cancel' };

export interface BridgesPointerContext {
  islands: BridgesIsland[];
  bridges: BridgesMap;
  bridgeKeys?: BridgeKey[];
  islandCenter: (island: BridgesIsland) => BridgesPoint;
  now?: number;
}

export interface BridgesPointerSnapshot {
  draggingFrom: BridgesIsland | null;
  dragPreviewKey: string | null;
  hoveredBridgeKey: string | null;
  hoveredIslandId: string | null;
  touchHoverIslandId: string | null;
  isTouchDragging: boolean;
  isTouchActive: boolean;
}

/**
 * How long a finger has to rest on an island before the dashed options are
 * revealed.
 *
 * Touching and swiping straight into a bridge is the normal way to play, and
 * that gesture used to flash the whole fan of dashed lines for the handful of
 * frames before the drag threshold was crossed. Waiting the reveal out costs a
 * deliberate hold nothing — a plain tap still reveals immediately on lift.
 */
export const TOUCH_HOVER_HOLD_MS = 250;

export function computeTouchDragThreshold(cellSize: number): number {
  const relative = 0.2 * cellSize;
  return Math.min(Math.max(relative, 8), Math.max(24, 0.35 * cellSize));
}

export class BridgesPointerController {
  private draggingFrom: BridgesIsland | null = null;
  private hoveredKeyS: string | null = null;
  private hoveredIslandId: string | null = null;
  private dragPreviewKeyS: string | null = null;
  private touchHoverIslandId: string | null = null;
  /** Waiting out {@link TOUCH_HOVER_HOLD_MS} before it becomes the reveal above. */
  private pendingTouchHoverIslandId: string | null = null;
  private touchHoverReadyAt = 0;

  private isTouchActive = false;
  private touchSuppressHoverUntil = 0;
  private isTouchDragging = false;
  /**
   * The pair the current preview is showing. onTouchMove deliberately keeps a
   * preview on screen when the raycast momentarily misses (to avoid flicker),
   * so touch-end has to commit what the player was actually shown rather than
   * re-deriving a target that may no longer resolve.
   */
  private previewKey: BridgeKey | null = null;
  private touchStartSvg: BridgesPoint | null = null;
  private touchDragThreshold = 12;
  private dragOrientation: 'h' | 'v' | null = null;
  private touchStateDebounceUntil = 0;

  private cellSize = 60;

  setCellSize(cellSize: number): void {
    this.cellSize = cellSize;
    this.touchDragThreshold = computeTouchDragThreshold(cellSize);
  }

  snapshot(): BridgesPointerSnapshot {
    return {
      draggingFrom: this.draggingFrom,
      dragPreviewKey: this.dragPreviewKeyS,
      hoveredBridgeKey: this.hoveredKeyS,
      hoveredIslandId: this.hoveredIslandId,
      touchHoverIslandId: this.touchHoverIslandId,
      isTouchDragging: this.isTouchDragging,
      isTouchActive: this.isTouchActive,
    };
  }

  isSuppressingHover(now: number = Date.now()): boolean {
    return this.isTouchActive || now < this.touchSuppressHoverUntil;
  }

  /**
   * When a pending touch reveal comes due, or `null` if none is waiting.
   *
   * The controller has no timer of its own — the owner reads this to schedule
   * the repaint that {@link settleTouchHover} then acts on.
   */
  pendingTouchHoverAt(): number | null {
    return this.pendingTouchHoverIslandId === null ? null : this.touchHoverReadyAt;
  }

  /** Promotes a pending reveal once the hold is up. Reports whether anything changed. */
  settleTouchHover(now: number = Date.now()): boolean {
    if (this.pendingTouchHoverIslandId === null || now < this.touchHoverReadyAt) return false;
    this.touchHoverIslandId = this.pendingTouchHoverIslandId;
    this.pendingTouchHoverIslandId = null;
    return true;
  }

  onMouseDownIsland(island: BridgesIsland): void {
    this.draggingFrom = island;
  }

  onEnterBridge(key: BridgeKey, now: number = Date.now()): void {
    if (this.isSuppressingHover(now)) return;
    this.hoveredKeyS = serializeKey(key);
  }

  onLeaveBridge(): void {
    this.hoveredKeyS = null;
  }

  onEnterIsland(island: BridgesIsland, now: number = Date.now()): void {
    if (this.isSuppressingHover(now)) return;
    this.hoveredIslandId = island.id;
  }

  onLeaveIsland(): void {
    this.hoveredIslandId = null;
  }

  onMouseMove(svg: BridgesPoint, ctx: BridgesPointerContext): BridgesPointerIntent {
    if (!this.draggingFrom) {
      this.clearPreview();
      return { kind: 'none' };
    }
    const target = this.pickRaycastTarget(this.draggingFrom, svg, ctx, null);
    if (!target) {
      this.clearPreview();
      return { kind: 'none' };
    }
    const k = normalizeKey(ctx.islands, { a: this.draggingFrom.id, b: target.id });
    if (!k) {
      this.clearPreview();
      return { kind: 'none' };
    }
    if (this.previewWouldCross(k, ctx)) {
      this.clearPreview();
      return { kind: 'none' };
    }
    this.dragPreviewKeyS = serializeKey(k);
    this.previewKey = k;
    return { kind: 'preview', key: k };
  }

  onMouseUp(svg: BridgesPoint, ctx: BridgesPointerContext): BridgesPointerIntent {
    let intent: BridgesPointerIntent = { kind: 'none' };
    if (this.draggingFrom) {
      const target = this.pickRaycastTarget(this.draggingFrom, svg, ctx, null);
      if (target) {
        intent = { kind: 'commit', key: { a: this.draggingFrom.id, b: target.id } };
      }
    }
    this.draggingFrom = null;
    this.clearPreview();
    return intent;
  }

  onMouseUpIsland(island: BridgesIsland): BridgesPointerIntent {
    if (!this.draggingFrom) return { kind: 'none' };
    const from = this.draggingFrom;
    this.draggingFrom = null;
    if (from.id === island.id) return { kind: 'none' };
    return { kind: 'commit', key: { a: from.id, b: island.id } };
  }

  onTouchStartIsland(island: BridgesIsland, svg: BridgesPoint | null, now: number = Date.now()): void {
    this.isTouchActive = true;
    this.touchSuppressHoverUntil = now + 700;
    this.hoveredKeyS = null;
    this.hoveredIslandId = null;
    this.clearPreview();
    this.draggingFrom = island;
    this.isTouchDragging = false;
    this.dragOrientation = null;
    this.touchStateDebounceUntil = 0;
    this.touchDragThreshold = computeTouchDragThreshold(this.cellSize);
    this.armTouchHover(island.id, now);
    this.touchStartSvg = svg ? { x: svg.x, y: svg.y } : null;
  }

  onTouchMove(svg: BridgesPoint, ctx: BridgesPointerContext, multiTouch = false): BridgesPointerIntent {
    if (!this.draggingFrom) return { kind: 'none' };
    const now = ctx.now ?? Date.now();
    this.isTouchActive = true;

    if (multiTouch) {
      this.cancelTouch(true, now);
      return { kind: 'cancel' };
    }

    if (!this.isTouchDragging && this.touchStartSvg) {
      const dx0 = svg.x - this.touchStartSvg.x;
      const dy0 = svg.y - this.touchStartSvg.y;
      const dist = Math.hypot(dx0, dy0);
      if (dist < this.touchDragThreshold) {
        // The finger is still resting on the island, so this is where the hold
        // can come due even if no timer fires.
        this.settleTouchHover(now);
        this.clearPreview();
        return { kind: 'none' };
      }
      if (now >= this.touchStateDebounceUntil) {
        this.isTouchDragging = true;
        this.touchHoverIslandId = null;
        this.pendingTouchHoverIslandId = null;
        this.dragOrientation = this.chooseDragOrientation(this.draggingFrom, dx0, dy0, ctx.islands);
        this.touchStateDebounceUntil = now + 120;
      }
    }

    const from = this.draggingFrom;
    const c = ctx.islandCenter(from);

    if (this.isTouchDragging && this.touchStartSvg) {
      const distToCenter = Math.hypot(svg.x - c.x, svg.y - c.y);
      const distToStart = Math.hypot(svg.x - this.touchStartSvg.x, svg.y - this.touchStartSvg.y);
      const returnThreshold = this.touchDragThreshold * 0.55;
      if (Math.min(distToCenter, distToStart) < returnThreshold && now >= this.touchStateDebounceUntil) {
        this.isTouchDragging = false;
        this.clearPreview();
        // Coming back to the island re-arms the hold rather than snapping the
        // dashed options straight back on, which would flicker on the way past.
        this.armTouchHover(from.id, now);
        this.dragOrientation = null;
        this.touchStateDebounceUntil = now + 120;
        return { kind: 'none' };
      }
    }

    const target = this.pickRaycastTarget(from, svg, ctx, this.dragOrientation);
    if (!target) {
      // keep existing preview to avoid blink
      return { kind: 'none' };
    }
    const k = normalizeKey(ctx.islands, { a: from.id, b: target.id });
    if (!k) return { kind: 'none' };
    if (this.previewWouldCross(k, ctx)) {
      return { kind: 'none' };
    }
    this.dragPreviewKeyS = serializeKey(k);
    this.previewKey = k;
    return { kind: 'preview', key: k };
  }

  onTouchEnd(svg: BridgesPoint | null, ctx: BridgesPointerContext): BridgesPointerIntent {
    let intent: BridgesPointerIntent = { kind: 'none' };
    if (this.draggingFrom && this.isTouchDragging) {
      const target = svg ? this.pickRaycastTarget(this.draggingFrom, svg, ctx, this.dragOrientation) : null;
      if (target) {
        intent = { kind: 'commit', key: { a: this.draggingFrom.id, b: target.id } };
      } else if (this.previewKey && this.dragPreviewKeyS) {
        // The raycast no longer resolves, but a preview is still on screen —
        // onTouchMove holds it deliberately to avoid flicker. Commit what the
        // player was actually shown instead of silently dropping the gesture.
        intent = { kind: 'commit', key: this.previewKey };
      }
    }
    // A tap is already a deliberate ask for the options, so lifting without a
    // drag reveals them at once instead of making the player hold again.
    if (!this.isTouchDragging && this.pendingTouchHoverIslandId !== null) {
      this.touchHoverIslandId = this.pendingTouchHoverIslandId;
    }
    this.pendingTouchHoverIslandId = null;
    this.draggingFrom = null;
    this.clearPreview();
    this.isTouchDragging = false;
    this.dragOrientation = null;
    this.isTouchActive = false;
    this.touchSuppressHoverUntil = (ctx.now ?? Date.now()) + 700;
    return intent;
  }

  onTouchCancel(now: number = Date.now()): void {
    this.cancelTouch(false, now);
  }

  onCycleBridgeRequested(key: BridgeKey): BridgesPointerIntent {
    this.hoveredKeyS = null;
    this.hoveredIslandId = null;
    this.clearPreview();
    this.touchHoverIslandId = null;
    this.pendingTouchHoverIslandId = null;
    return { kind: 'commit', key };
  }

  private armTouchHover(islandId: string, now: number): void {
    this.touchHoverIslandId = null;
    this.pendingTouchHoverIslandId = islandId;
    this.touchHoverReadyAt = now + TOUCH_HOVER_HOLD_MS;
  }

  private clearPreview(): void {
    this.dragPreviewKeyS = null;
    this.previewKey = null;
  }

  private cancelTouch(keepTapPreview: boolean, now: number): void {
    this.draggingFrom = null;
    this.clearPreview();
    this.isTouchDragging = false;
    this.isTouchActive = false;
    this.dragOrientation = null;
    this.touchSuppressHoverUntil = now + 300;
    this.pendingTouchHoverIslandId = null;
    if (!keepTapPreview) this.touchHoverIslandId = null;
  }

  private pickRaycastTarget(
    from: BridgesIsland,
    svg: BridgesPoint,
    ctx: BridgesPointerContext,
    lockedOrientation: 'h' | 'v' | null
  ): BridgesIsland | null {
    const c = ctx.islandCenter(from);
    const dx = svg.x - c.x;
    const dy = svg.y - c.y;
    if (lockedOrientation === 'h') {
      return raycast(ctx.islands, from, dx >= 0 ? 1 : -1, 0);
    }
    if (lockedOrientation === 'v') {
      return raycast(ctx.islands, from, 0, dy >= 0 ? 1 : -1);
    }
    if (Math.abs(dx) >= Math.abs(dy)) {
      return raycast(ctx.islands, from, dx >= 0 ? 1 : -1, 0);
    }
    return raycast(ctx.islands, from, 0, dy >= 0 ? 1 : -1);
  }

  private chooseDragOrientation(
    from: BridgesIsland,
    dx0: number,
    dy0: number,
    islands: BridgesIsland[]
  ): 'h' | 'v' {
    const preferH = Math.abs(dx0) >= Math.abs(dy0);
    const hasH = !!(raycast(islands, from, 1, 0) || raycast(islands, from, -1, 0));
    const hasV = !!(raycast(islands, from, 0, 1) || raycast(islands, from, 0, -1));
    if (preferH && hasH) return 'h';
    if (!preferH && hasV) return 'v';
    if (hasH) return 'h';
    return 'v';
  }

  private previewWouldCross(k: BridgeKey, ctx: BridgesPointerContext): boolean {
    const sk = serializeKey(k);
    const current = ctx.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next <= current) return false;
    const bridgeKeys = ctx.bridgeKeys ?? computeNeighborPairs(ctx.islands);
    return createsCrossing(ctx.islands, bridgeKeys, ctx.bridges, k, next);
  }
}
